<template>
  <div v-if="vacancyCompleted">
    <VRow class="flex-md-row-reverse" no-gutters>
      <VCol cols="12" md="5">
        <div class="text-md-right">
          <span v-if="vacancy.publishedFrom">
            {{ $t('published') }}: {{ publishedFromFormatted }} <br>
          </span>
          <span v-if="vacancy.publishedTill" class="mb-4 d-inline-block">
            <DatePicker
              v-model="datePicker"
              :date="new Date(vacancy.publishedTill).toISOString().substr(0, 10)"
              :loading="updateLoading"
              :min="tomorrow"
              @change="updatePublishedTill"
            >
              <template #activator="{ on: { click } }">
                <span v-if="isArchiveVacancy">{{ $t('expired') }}: </span>
                <span v-else>{{ $t('expires') }}: </span>
                <span class="pseudo-link" @click="click">{{ publishedTillFormatted }}</span>
              </template>
            </DatePicker>
          </span>
        </div>
      </VCol>
      <VCol cols="12" md="7">
        <div class="d-flex">
          <VacancyCardButtonsPanel
            :vacancy="vacancy"
            :limited-vacancies="limitedVacancies"
            @openDialogMaximumVacancies="openDialogMaximumVacancies"
          />
        </div>
      </VCol>
    </VRow>
    <VRow v-if="vacancyCompleted">
      <VCol
        cols="12"
        md="10"
        lg="8"
        class="mb-4"
      >
        <VRow
          wrap
          no-gutters
          align="center"
          class="mb-4 mb-md-0"
        >
          <VCol cols="12" sm="auto">
            <h2 class="title mr-sm-8">
              {{ $t('vacancyDescription') }}
            </h2>
          </VCol>
          <VCol cols="12" sm="auto">
            <RouterLink
              v-if="canEdit"
              :to="{ name: 'vacancy-edit', params: { id: vacancy.id }}"
            >
              {{ $t('editDescription') }}
            </RouterLink>
            <span
              v-if="isArchiveVacancy"
              class="body-2"
            >
              {{ $t('vacancyArchived') }}
            </span>
          </VCol>
        </VRow>
        <VRow class="mb-4">
          <VCol cols="12" md="6">
            <div class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('salary') }}:
              </span>
              <span>
                <VacancySalary :salary="vacancy.salary" />
              </span>
            </div>
            <div class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('experience') }}:
              </span>
              <span>
                {{ $t(`experience.${vacancy.experience}`) }}
              </span>
            </div>
            <div class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('employment') }}:
              </span>
              <span>
                {{ employment }}
              </span>
            </div>
            <div class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('schedule') }}:
              </span>
              <span>{{ schedule }}</span>
            </div>
            <div class="mb-2" v-if="skills">
              <span class="mr-2 font-weight-medium">
                {{ $t('keySkills') }}:
              </span>
              <span>{{ skills }}</span>
            </div>
            <div v-if="address" class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('workAddress') }}:
              </span>
              <span>{{ address }}</span>
            </div>
          </VCol>

          <VCol v-if="vacancy.contactPerson" cols="12" md="6">
            <div class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('contactPerson') }}:
              </span>
              <span>{{ vacancy.contactPerson.name }}</span>
            </div>
            <div class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('email') }}:</span>
              <a
                :href="`mailto:${vacancy.contactPerson.email}`"
              >{{ vacancy.contactPerson.email }}</a>
            </div>
            <div class="mb-2">
              <span class="mr-2 font-weight-medium">
                {{ $t('phone') }}:
              </span>
              <span>{{ vacancy.contactPerson.phone }}</span>
            </div>
            <div class="mb-2" v-if="vacancy.contactPerson.comment">
              <span class="mr-2 font-weight-medium">
                {{ $t('comment') }}:</span>
              <span>{{ vacancy.contactPerson.comment }}</span>
            </div>
          </VCol>
        </VRow> <!-- eslint-disable-next-line -->
        <div v-html="vacancy.description" />

        <VRow
          no-gutters
          wrap
          align="center"
          class="mt-8 mb-4"
        >
          <VCol cols="12" sm="auto">
            <h2 class="title mr-sm-8">
              {{ $t('interviewQuestions') }}
            </h2>
          </VCol>
          <VCol v-if="canEdit" cols="12" sm="auto">
            <RouterLink
              :to="redactingQuestionsRoute"
            >
              {{ $t('editQuestions') }}
            </RouterLink>
          </VCol>
        </VRow>
        <div
          v-if="vacancy.script && vacancy.script.questions"
          class="font-weight-medium"
        >
          <p
            v-for="(question, index) in vacancy.script.questions"
            :key="`${question.id}.${question.type}`"
          >
            {{ index+1 }}. {{ question.question }}
          </p>
        </div>

        <VRow
          no-gutters
          class="mt-7 mb-4"
          align="center"
          wrap
        >
          <VCol cols="12" sm="auto">
            <h2 class="title mr-sm-8">
              {{ $t('questionsAndAnswers') }}
            </h2>
          </VCol>
          <VCol v-if="canEdit" cols="12" sm="auto">
            <RouterLink
              :to="{ name: 'vacancy-faq-edit', params: { id: vacancy.id }}"
            >
              {{ $t('editQuestions') }}
            </RouterLink>
          </VCol>
        </VRow>
        <VExpansionPanels v-if="vacancy.faq.length" multiple class="mb-10">
          <VExpansionPanel
            v-for="(item, index) in vacancy.faq"
            ref="item"
            :key="item.id"
            @change="scrollTo(index)"
          >
            <VExpansionPanelHeader class="subtitle-2">
              {{ index+1 }}. {{ item.question }}
            </VExpansionPanelHeader>
            <VExpansionPanelContent class="body-2">
              {{ item.answer }}
            </VExpansionPanelContent>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
      <VCol cols="12" lg="4">
        <IntegrationsVacancy
          :vacancy-id="vacancy.id"
          :external-vacancies="vacancy.externalVacancies"
        />
      </VCol>
    </VRow>
    <VDialog
      v-model="dialogMaximumVacancies"
      persistent
      max-width="500"
    >
      <DialogLimitedVacancies
        :title="maxVacanciesTitle"
        :plan-title="planTitle"
        :limited-vacancies="limitedVacancies"
        @close="cancelDialogMaximumVacancies"
      />
    </VDialog>
  </div>
</template>

<script>
import addDays from 'date-fns/addDays';
import IntegrationsVacancy from '@/components/Integrations/IntegrationsVacancy';
import RouterController from '@/orm/models/Wizard/RouterController';
import DialogLimitedVacanciesMixin from '@/mixins/DialogLimitedVacanciesMixin';
import { format } from '../../date-utils';
import VacancyCardButtonsPanel from './VacancyCardButtonsPanel.vue';
import DatePicker from '../Input/DatePicker.vue';
import { vacancyStatus } from '../../enums';
import { safeGet } from '../../functions';
import ValidationMixin from '../../mixins/ValidationMixin';
import VacancySalary from './VacancySalary.vue';
import Vacancy from '../../orm/models/Vacancy/Vacancy';

export default {
  name: 'VacancyCard',
  metaInfo() {
    return {
      title: this.vacancy.title,
    };
  },
  components: {
    IntegrationsVacancy,
    VacancySalary,
    VacancyCardButtonsPanel,
    DatePicker,
  },
  mixins: [ValidationMixin, DialogLimitedVacanciesMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      datePicker: false,
      tomorrow: addDays(new Date(), 1).toISOString().substr(0, 10),
      updateLoading: false,
      isVacancyCard: true,
    };
  },
  computed: {
    isNewExternal() {
      return !this.vacancy.wizzardState && this.vacancy.externalVacancies?.length;
    },
    skills() {
      return this.vacancy.skills.join(', ');
    },
    canEdit() {
      return this.vacancy.status !== vacancyStatus.ARCHIVE;
    },
    isArchiveVacancy() {
      return this.vacancy.status === vacancyStatus.ARCHIVE;
    },
    address() {
      return safeGet(this.vacancy, ['address', 'address']);
    },
    employment() {
      if (!Array.isArray(this.vacancy.employment)) {
        return '';
      }

      const employments = this.vacancy.employment?.map((e) => this.$t(`employment.${e}`)) || [];
      return employments.join(', ');
    },
    schedule() {
      if (!Array.isArray(this.vacancy.schedule)) {
        return '';
      }
      const schedules = this.vacancy.schedule?.map((e) => this.$t(`schedule.${e}`)) || [];
      return schedules.join(', ');
    },
    publishedFromFormatted() {
      return this.vacancy.publishedFrom
        ? format(new Date(this.vacancy.publishedFrom), 'd MMMM yyyy')
        : this.$t('noData');
    },
    publishedTillFormatted() {
      return this.vacancy.publishedTill
        ? format(new Date(this.vacancy.publishedTill), 'd MMMM yyyy')
        : this.$t('noData');
    },
    interviewerId() {
      return safeGet(this.vacancy, ['script', 'interviewer', 'id']);
    },
    vacancyCompleted() {
      return !!this.interviewerId;
    },
    backRoute() {
      const { wizardState } = this.vacancy;
      const { step } = wizardState;
      const controller = new RouterController({ step });
      return controller.makeRoute({ step, wizardState }) || {
        name: 'vacancy-preset',
        params: {
          vacancyId: this.vacancy.id,
        },
      };
    },
    redactingQuestionsRoute() {
      return this.vacancy.isAiInterviewCompiled
        ? {
          name: 'vacancy-ai-questions-edit',
          params: { vacancyId: this.vacancy.id },
        }
        : { name: 'vacancy-questions-edit', params: { id: this.vacancy.id } };
    },
  },
  created() {
    this.onCreate();
  },
  methods: {
    async onCreate() {
      if ((this.vacancy.isAiInterviewCompiling || this.vacancy.isAiInterviewRecompiling) && !this.vacancy.isAiInterviewCompiled) {
        await this.$router.push({
          name: 'vacancy-publish',
          params: {
            vacancyId: this.vacancy.id,
          },
        });
        return;
      }
      if (!this.vacancyCompleted) {
        await this.$router.push(this.backRoute);
        return;
      }
      if (this.vacancy.isAiInterviewRecompiling) {
        await this.$router.push({
          name: 'vacancy-ai-questions-edit',
          params: {
            vacancyId: this.vacancy.id,
          },
        });
      }
    },
    async updatePublishedTill(date) {
      await this.withApiErrorHandling(async () => {
        await Vacancy.api().update(this.vacancy.id, { publishedTill: date });
        this.$toast.success(this.$t('publicationDateChangeTip'));
        this.datePicker = false;
      }, (error) => {
        if (error.errors && error.errors.publishedTill) {
          this.$toast.error(error.errors.publishedTill.join(', '));
        } else {
          this.$toast.error(error.message);
        }
      }, 'updateLoading');
    },
    scrollTo(id) {
      this.$vuetify.goTo(this.$refs.item[id].$el, {
        offset: 70,
      });
    },
  },
};
</script>

<i18n locale="en" lang="yaml">
  published: Published
  expires: Expires
  expired: Moved to archive
  vacancyDescription: Job description
  editDescription: Edit description
  vacancyArchived: Job is archived
  salary: Salary
  experience: Experience
  experience.beginner: No experience
  experience.1-3 years: 1-3 years
  experience.3-6 years: 3-6 years
  experience.6+ years: 6+ years
  employment: Employment
  employment.fulltime: full time
  employment.parttime: part time
  employment.project: contract/temporary work
  employment.voluntary: voluntary
  employment.internship: internship
  schedule: Work schedule
  schedule.regular: full day
  schedule.shift: shift
  schedule.flex: flexible
  schedule.remote: remote work
  schedule.watch: remote shift
  workAddress: Work address
  contactPerson: Contact person
  email: E-mail
  phone: Phone
  comment: Comment
  interviewQuestions: Interview questions
  editQuestions: Edit questions
  questionsAndAnswers: Questions/Answers about the vacancy
  noData: No data
  publicationDateChangeTip: Publication date successfully changed
  keySkills: Key skills
  maxVacanciesCopyTitle: "You cannot copy the job"
  maxVacanciesRestoreTitle: 'You cannot restore the job'
</i18n>

<i18n locale="ru" lang="yaml">
  published: Опубликована
  expires: Истекает
  expired: Перенесена в архив
  vacancyDescription: Описание вакансии
  editDescription: Редактировать описание
  vacancyArchived: Вакансия перенесена в архив
  salary: Зарплата
  experience: Опыт работы
  experience.beginner: начало карьеры
  experience.1-3 years: 1-3 года
  experience.3-6 years: 3-6 лет
  experience.6+ years: более 6 лет
  employment: Занятость
  employment.fulltime: полная занятость
  employment.parttime: частичная занятость
  employment.project: проектная/временная работа
  employment.voluntary: волонтерство
  employment.internship: стажировка
  schedule: График работы
  schedule.regular: полный день
  schedule.shift: сменный график
  schedule.flex: гибкий график
  schedule.remote: удаленная работа
  schedule.watch: вахтовый метод
  workAddress: Место работы
  contactPerson: Контактное лицо
  email: Эл. почта
  phone: Телефон
  comment: Комментарий
  interviewQuestions: Вопросы для собеседования
  editQuestions: Редактировать вопросы
  questionsAndAnswers: Вопросы-ответы о вакансии
  noData: Нет данных
  publicationDateChangeTip: Дата публикации вакансии успешно изменена
  noCompleted: Вы не завершили создание вакансии.
  imported: Необходимо создать скрипт вакансии
  next: 'Продолжить?'
  keySkills: Ключевые навыки
  maxVacanciesCopyTitle: 'Вы не можете скопировать вакансию'
  maxVacanciesRestoreTitle: 'Вы не можете восстановить вакансию'
</i18n>
